import React, { useEffect, useState } from "react";
import {
  Table,
  Typography,
  Button,
  Space,
  Tooltip,
  Input,
  Modal,
  message,
  Drawer,
  Tag,
  Badge,
  Card,
  Avatar,
  Divider,
  Statistic,
  Row,
  Col,
  theme,
  Progress,
  Dropdown,
  Segmented
} from "antd";
import {
  EyeOutlined,
  DeleteOutlined,
  HomeFilled,
  SearchOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ReloadOutlined,
  GlobalOutlined,
  ShopOutlined,
  TeamOutlined,
  PlusOutlined,
  FilterOutlined,
  ExportOutlined,
  StarOutlined,
  WarningOutlined,
  MoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  TableOutlined,
  AppstoreOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import API from "../../API/API";
import AddYard from "./AddYard";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Building2Icon, BuildingIcon } from "lucide-react";
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { useToken } = theme;

const ViewyardDetails = () => {
  const { token } = useToken();
  const navigate = useNavigate();
  
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [showDeletedDrawer, setShowDeletedDrawer] = useState(false);
  const [deletedYards, setDeletedYards] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [selectedDeletedKeys, setSelectedDeletedKeys] = useState([]);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'card'
  const [stats, setStats] = useState({
    total: 0,
    withContact: 0,
    withEmail: 0,
    withPhone: 0
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [deletedPagination, setDeletedPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const fetchYards = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      if (!userData?.id) {
        console.error("User authentication data not found");
        setLoading(false);
        return;
      }

      const params = {
        page,
        limit: pageSize,
        sortBy: "createdAt",
        order: "desc",
      };

      if (searchTerm?.trim()) {
        params.search = searchTerm.trim();
      }

      const response = await API.yardGetAll(params);
      if (response?.status) {
        setYards(response.data);
        
        const stats = response.data.reduce((acc, yard) => ({
          total: acc.total + 1,
          withContact: acc.withContact + (yard.contactPerson?.length > 0 ? 1 : 0),
          withEmail: acc.withEmail + (yard.email?.length > 0 ? 1 : 0),
          withPhone: acc.withPhone + (yard.contactNumbers?.length > 0 ? 1 : 0)
        }), { total: 0, withContact: 0, withEmail: 0, withPhone: 0 });
        
        setStats(stats);
        
        setPagination((prev) => ({
          ...prev,
          current: response.pagination.currentPage,
          total: response.pagination.totalRecords,
        }));
      } else {
        setYards([]);
      }
    } catch (error) {
      console.error("Failed to fetch yard data:", error);
      setYards([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedYards = async (page = deletedPagination.current, pageSize = deletedPagination.pageSize) => {
    setLoadingDeleted(true);
    try {
      const response = await API.GetDeletedYard({ page, limit: pageSize });
      if (response?.success) {
        setDeletedYards(response.data);
        setDeletedPagination((prev) => ({
          ...prev,
          current: response.pagination.currentPage,
          total: response.pagination.totalItems,
        }));
      } else {
        message.error("Failed to load deleted yards.");
      }
    } catch (error) {
      console.error("Error fetching deleted yards:", error);
      message.error("Something went wrong.");
    } finally {
      setLoadingDeleted(false);
    }
  };

  useEffect(() => {
    fetchYards(1, pagination.pageSize);
  }, [searchTerm]);

  const formatPhoneNumber = (num) => {
    if (!num) return "";
    const cleaned = num.replace(/\D/g, "");
    const number = cleaned.length === 11 && cleaned.startsWith("1") ? cleaned.slice(1) : cleaned;

    if (number.length === 10) {
      const area = number.slice(0, 3);
      const mid = number.slice(3, 6);
      const last = number.slice(6, 10);
      return `(${area}) ${mid}-${last}`;
    }

    return num;
  };

  const handleDeleteSelected = () => {
    Modal.confirm({
      title: "Delete Selected Yards?",
      icon: <WarningOutlined style={{ color: '#ff4d4f' }} />,
      content: `Are you sure you want to delete ${selectedRowKeys.length} selected yard(s)?`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const response = await API.SoftDeleteYard(selectedRowKeys);
          if (response?.status) {
            message.success(`${selectedRowKeys.length} yard(s) deleted successfully.`);
            setSelectedRowKeys([]);
            fetchYards(pagination.current, pagination.pageSize);
          } else {
            message.error(response.message || "Failed to delete yard(s).");
          }
        } catch (error) {
          console.error("Delete error:", error);
          message.error("Something went wrong while deleting.");
        }
      },
    });
  };

  const restoreyard = async (id) => {
    const yard = deletedYards.find((item) => item._id === id);
    Modal.confirm({
      title: "Restore Yard?",
      content: `Are you sure you want to restore "${yard?.yardName}"?`,
      okText: "Yes, Restore",
      cancelText: "No",
      onOk: async () => {
        try {
          const res = await API.RestoreYard(id);
          if (res?.success) {
            message.success("Yard restored successfully!");
            fetchDeletedYards(deletedPagination.current, deletedPagination.pageSize);
            fetchYards(pagination.current, pagination.pageSize);
          } else {
            message.error("Failed to restore.");
          }
        } catch (error) {
          console.error("Restore error:", error);
          message.error("Error restoring yard.");
        }
      },
    });
  };

  const maxContacts = yards.reduce((max, yard) => {
    return Math.max(
      max,
      yard.contactPerson?.length || 0,
      yard.email?.length || 0,
      yard.contactNumbers?.length || 0
    );
  }, 0);

  // Modern Table Columns with Enhanced Design
  const columns = [
    {
      title: "Yard Information",
      dataIndex: "yardName",
      key: "yardName",
      width: 280,
      fixed: 'left',
      render: (text, record) => (
        <motion.div
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.2 }}
          style={{ display: 'flex', alignItems: 'center', gap: 14 }}
        >
          <Avatar 
            size={48} 
            style={{ 
              background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
              boxShadow: '0 4px 12px rgba(22,119,255,0.2)',
              flexShrink: 0
            }}
            icon={<ShopOutlined style={{ fontSize: 24 }} />}
          />
          <div style={{ flex: 1 }}>
            <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
              {text}
            </Text>
            <Space size={4}>
              <Badge 
                status="processing" 
                text={
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ID: {record._id?.slice(-6)}
                  </Text>
                } 
              />
            </Space>
          </div>
        </motion.div>
      ),
    },
    
    ...Array.from({ length: Math.min(maxContacts, 3) }, (_, i) => ({
      title: (
        <div style={{ textAlign: 'center' }}>
          <Avatar 
            size={24} 
            style={{ 
              backgroundColor: i === 0 ? token.colorPrimary : token.colorSuccess,
              marginBottom: 4
            }}
          >
            {i + 1}
          </Avatar>
          <div>
            <Text strong style={{ fontSize: 13 }}>Contact {i + 1}</Text>
          </div>
        </div>
      ),
      key: `contact_${i}`,
      width: 260,
     // In the columns array, update the contact render function:
render: (record) => {
  const person = record.contactPerson?.[i];
  const email = record.email?.[i];
  const phone = record.contactNumbers?.[i];
  const extension = record.contactPersonExtension?.[i]; // ADD THIS

  if (!person && !email && !phone) return (
    <div style={{ textAlign: 'center', padding: 16 }}>
      <Text type="secondary" style={{ fontSize: 12 }}>No contact info</Text>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: i * 0.05 }}
    >
      <Card 
        size="small" 
        style={{ 
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          background: token.colorBgLayout,
          cursor: 'pointer',
          transition: 'all 0.3s ease'
        }}
        hoverable
        bodyStyle={{ padding: '12px' }}
      >
        {person && (
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ 
              width: 28, 
              height: 28, 
              borderRadius: '50%', 
              background: `linear-gradient(135deg, ${token.colorPrimary}20, ${token.colorSuccess}20)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <UserOutlined style={{ color: token.colorPrimary, fontSize: 14 }} />
            </div>
            <Text strong style={{ fontSize: 13 }}>{person}</Text>
          </div>
        )}
        
        {email && (
          <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
            <MailOutlined style={{ color: token.colorWarning, fontSize: 12 }} />
            <a 
              href={`mailto:${email}`} 
              style={{ fontSize: 12, color: token.colorInfo, textDecoration: 'none' }}
              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
            >
              {email.length > 25 ? `${email.substring(0, 25)}...` : email}
            </a>
          </div>
        )}
        
        {phone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PhoneOutlined style={{ color: token.colorSuccess, fontSize: 12 }} />
            <Text style={{ fontSize: 12, fontFamily: 'monospace' }}>
              {formatPhoneNumber(phone)}
            </Text>
            {/* ADD EXTENSION HERE */}
            {extension && (
              <Tag 
                color="purple" 
                style={{ 
                  marginLeft: 4, 
                  fontSize: 10, 
                  borderRadius: 10,
                  padding: '0 6px',
                  lineHeight: '18px'
                }}
              >
                ext: {extension}
              </Tag>
            )}
          </div>
        )}
      </Card>
    </motion.div>
  );
},
    })),
    
    {
      title: "Location",
      key: "location",
      width: 260,
      render: (_, record) => {
        const addr = record.yardAddress;
        if (!addr) return <Text type="secondary">—</Text>;
        
        return (
          <motion.div whileHover={{ x: 4 }} transition={{ duration: 0.2 }}>
            <Card 
              size="small" 
              style={{ 
                borderRadius: 12,
                border: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorBgLayout
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <Space direction="vertical" size={6}>
                <Space>
                  <EnvironmentOutlined style={{ color: token.colorError }} />
                  <Text strong style={{ fontSize: 13 }}>
                    {[addr.address, addr.street].filter(Boolean).join(", ")}
                  </Text>
                </Space>
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 22 }}>
                  {[addr.city, addr.state, addr.zip_code].filter(Boolean).join(", ")}
                </Text>
                {addr.country && (
                  <Tag 
                    color="blue" 
                    style={{ marginLeft: 22, marginTop: 4, fontSize: 11 }}
                  >
                    {addr.country}
                  </Tag>
                )}
              </Space>
            </Card>
          </motion.div>
        );
      },
    },
    
    {
    title: 'Date Info',
    key: 'dateInfo',
    width: 200,
    sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    render: (_, record) => (
        <Tooltip
            title={
                <>
                    <div><strong>Created:</strong> {record.createdAt ? dayjs(record.createdAt).format('MM-DD-YYYY hh:mm A') : 'N/A'}</div>
                    <div><strong>Updated:</strong> {record.updatedAt ? dayjs(record.updatedAt).format('MM-DD-YYYY hh:mm A') : 'N/A'}</div>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

                {/* Created */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#F1EFE8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <ClockCircleOutlined style={{ fontSize: 13, color: '#888780' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                            {record.createdAt ? dayjs(record.createdAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                            Created · {record.createdAt ? dayjs(record.createdAt).fromNow() : '—'}
                        </Text>
                    </div>
                </div>

                {/* Updated */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#E6F1FB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <SyncOutlined style={{ fontSize: 13, color: '#185FA5' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#185FA5' }}>
                            {record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                            Updated · {record.updatedAt ? dayjs(record.updatedAt).fromNow() : '—'}
                        </Text>
                    </div>
                </div>

            </div>
        </Tooltip>
    ),
},
    
    {
      title: "",
      key: "actions",
      width: 80,
      fixed: 'right',
      render: (record) => (
        <Space>
          <Tooltip title="View Details">
            <Button 
              type="primary"
              shape="circle"
              icon={<EyeOutlined />} 
              onClick={() => navigate(`/yard-details/${record._id}`)}
              style={{
                background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
                border: 'none',
                boxShadow: '0 2px 8px rgba(22,119,255,0.3)'
              }}
            />
          </Tooltip>
       
        </Space>
      ),
    },
  ];

  const deletedColumns = [
    {
      title: "Yard",
      dataIndex: "yardName",
      key: "yardName",
      render: (text, record) => (
        <Space size={12}>
          <Avatar 
            size={40} 
            icon={<ShopOutlined />} 
            style={{ background: token.colorError, boxShadow: '0 2px 8px rgba(255,77,79,0.2)' }}
          />
          <div>
            <Text strong style={{ fontSize: 14 }}>{text}</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>Deleted: {new Date(record.updatedAt).toLocaleDateString()}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Location",
      key: "location",
      render: (_, record) => (
        <Tag icon={<EnvironmentOutlined />} color="orange">
          {record.yardAddress?.city || '—'}, {record.yardAddress?.state || '—'}
        </Tag>
      ),
    },
    {
      title: "Contacts",
      key: "contacts",
      render: (_, record) => (
        <Badge 
          count={record.contactPerson?.length || 0} 
          showZero
          style={{ backgroundColor: token.colorWarning }}
        />
      ),
    },
    {
      title: "",
      key: "action",
      render: (_, record) => (
        <Button 
          type="primary" 
          ghost 
          icon={<ReloadOutlined />} 
          onClick={() => restoreyard(record._id)}
          style={{ borderRadius: 20 }}
        >
          Restore
        </Button>
      ),
    },
  ];

  // Card View Component
  const CardView = () => (
    <Row gutter={[24, 24]}>
      <AnimatePresence>
        {yards.map((yard, index) => (
          <Col xs={24} sm={12} lg={8} xl={6} key={yard._id}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -8 }}
            >
              <Card
                hoverable
                style={{
                  borderRadius: 20,
                  overflow: 'hidden',
                  border: `1px solid ${token.colorBorderSecondary}`,
                  transition: 'all 0.3s ease'
                }}
                cover={
                  <div style={{
                    height: 120,
                    background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    <ShopOutlined style={{ fontSize: 48, color: 'white', opacity: 0.9 }} />
                    <Badge 
                      count="Active" 
                      style={{ 
                        position: 'absolute', 
                        top: 12, 
                        right: 12,
                        backgroundColor: token.colorSuccess 
                      }} 
                    />
                  </div>
                }
                actions={[
                  <Tooltip title="View Details">
                    <EyeOutlined key="view" onClick={() => navigate(`/yard-details/${yard._id}`)} />
                  </Tooltip>,
                  <Tooltip title="Edit">
                    <Building2Icon key="edit" />
                  </Tooltip>,
                  <Tooltip title="Share">
                    <GlobalOutlined key="share" />
                  </Tooltip>,
                ]}
              >
                <Card.Meta
                  title={
                    <Text strong style={{ fontSize: 16 }}>
                      {yard.yardName}
                    </Text>
                  }
                  description={
                    <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 8 }}>
                      <Space>
                        <EnvironmentOutlined style={{ color: token.colorError }} />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {yard.yardAddress?.city}, {yard.yardAddress?.state}
                        </Text>
                      </Space>
                      {yard.contactPerson?.[0] && (
                        <Space>
                          <UserOutlined />
                          <Text style={{ fontSize: 12 }}>{yard.contactPerson[0]}</Text>
                        </Space>
                      )}
                      {yard.link && (
                        <Tag 
                          icon={<GlobalOutlined />} 
                          color="processing"
                          style={{ cursor: 'pointer', marginTop: 4 }}
                          onClick={() => window.open(yard.link, '_blank')}
                        >
                          Website
                        </Tag>
                      )}
                    </Space>
                  }
                />
                <Divider style={{ margin: '12px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    <CalendarOutlined /> {new Date(yard.createdAt).toLocaleDateString()}
                  </Text>
                  <Badge 
                    count={`${yard.contactPerson?.length || 0} Contacts`}
                    style={{ backgroundColor: token.colorPrimary }}
                  />
                </div>
              </Card>
            </motion.div>
          </Col>
        ))}
      </AnimatePresence>
    </Row>
  );

  return (
    <div style={{ 
      padding: 24, 
      minHeight: "100vh", 
      background: `linear-gradient(135deg, ${token.colorBgContainer} 0%, ${token.colorBgLayout} 100%)`
    }}>
      {/* Modern Header with Stats */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card 
          style={{ 
            marginBottom: 24, 
            borderRadius: 24,
            background: `linear-gradient(135deg, ${token.colorPrimary} 0%, ${token.colorPrimaryActive} 100%)`,
            border: 'none'
          }}
          bodyStyle={{ padding: '32px' }}
        >
          <Row gutter={[24, 24]} align="middle">
            <Col xs={24} md={12}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                <Avatar 
                  size={72} 
                  style={{ 
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                  }}
                  icon={<ShopOutlined style={{ fontSize: 36 }} />}
                />
                <div>
                  <Title level={2} style={{ margin: 0, color: 'white', fontWeight: 700 }}>
                    Yard Management
                  </Title>
                  <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14 }}>
                    Manage and monitor all your collaborating yards in one place
                  </Text>
                </div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Card 
                    style={{ 
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 16,
                      border: 'none'
                    }}
                    bodyStyle={{ padding: '12px', textAlign: 'center' }}
                  >
                    <Statistic 
                      title={<Text style={{ color: 'white' }}>Total Yards</Text>}
                      value={stats.total}
                      valueStyle={{ color: 'white', fontSize: 28, fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card 
                    style={{ 
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 16,
                      border: 'none'
                    }}
                    bodyStyle={{ padding: '12px', textAlign: 'center' }}
                  >
                    <Statistic 
                      title={<Text style={{ color: 'white' }}>Active Contacts</Text>}
                      value={stats.withContact}
                      valueStyle={{ color: 'white', fontSize: 28, fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
                <Col span={8}>
                  <Card 
                    style={{ 
                      background: 'rgba(255,255,255,0.15)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 16,
                      border: 'none'
                    }}
                    bodyStyle={{ padding: '12px', textAlign: 'center' }}
                  >
                    <Statistic 
                      title={<Text style={{ color: 'white' }}>With Email</Text>}
                      value={stats.withEmail}
                      valueStyle={{ color: 'white', fontSize: 28, fontWeight: 'bold' }}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Card>
      </motion.div>

      {/* Search and Actions Bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card 
          style={{ 
            marginBottom: 24, 
            borderRadius: 20,
            border: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorBgContainer
          }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col xs={24} md={8}>
              <Input
                placeholder="Search yards, contacts, or locations..."
                prefix={<SearchOutlined style={{ color: token.colorTextSecondary }} />}
                style={{ 
                  borderRadius: 12,
                  border: `1px solid ${token.colorBorderSecondary}`,
                  background: token.colorBgContainer
                }}
                allowClear
                onChange={(e) => setSearchTerm(e.target.value)}
                size="large"
              />
            </Col>
            <Col xs={24} md="auto">
              <Space wrap size={12}>
                <Segmented
                  value={viewMode}
                  onChange={setViewMode}
                  options={[
                    { label: <><TableOutlined /> Table</>, value: 'table' },
                    { label: <><AppstoreOutlined /> Cards</>, value: 'card' },
                  ]}
                  style={{ borderRadius: 12 }}
                />
                
                {selectedRowKeys.length > 0 && (
                  <Badge count={selectedRowKeys.length} color="red">
                    <Button 
                      danger 
                      onClick={handleDeleteSelected}
                      icon={<DeleteOutlined />}
                      size="large"
                      style={{ borderRadius: 12 }}
                    >
                      Delete Selected
                    </Button>
                  </Badge>
                )}

                <Button
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setShowDeletedDrawer(true);
                    fetchDeletedYards();
                  }}
                  size="large"
                  style={{
                    borderRadius: 12,
                    background: token.colorWarningBg,
                    borderColor: token.colorWarning,
                    color: token.colorWarning
                  }}
                >
                  Recycle Bin ({deletedPagination.total})
                </Button>

                <AddYard refreshYards={fetchYards} />
              </Space>
            </Col>
          </Row>
        </Card>
      </motion.div>

      {/* Main Content - Table or Card View */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card 
          style={{ 
            borderRadius: 20,
            border: `1px solid ${token.colorBorderSecondary}`,
            background: token.colorBgContainer,
            overflow: 'hidden'
          }}
          bodyStyle={{ padding: viewMode === 'table' ? 0 : 24 }}
        >
          {viewMode === 'table' ? (
            <Table
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
                columnWidth: 40,
              }}
              columns={columns}
              dataSource={yards}
              loading={loading}
              rowKey="_id"
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                position: ['topRight'], 
                showTotal: (total, range) => (
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Showing {range[0]}-{range[1]} of {total} yards
                  </Text>
                ),
                onChange: (page, pageSize) => {
                  setPagination((prev) => ({ ...prev, current: page, pageSize }));
                  fetchYards(page, pageSize);
                },
                style: { padding: '16px 24px' }
              }}
              bordered={false}
              style={{ 
                background: "transparent"
              }}
              scroll={{
                x: 'max-content',
                y: 'calc(100vh - 450px)'
              }}
              rowClassName={(record, index) => 
                index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
              }
            />
          ) : (
            <CardView />
          )}
        </Card>
      </motion.div>

      {/* Deleted Yards Drawer */}
      <Drawer
        title={
          <Space>
            <Avatar 
              size={48} 
              style={{ background: token.colorErrorBg, color: token.colorError }}
              icon={<DeleteOutlined />}
            />
            <div>
              <Title level={4} style={{ margin: 0 }}>Recycle Bin</Title>
              <Text type="secondary">Restore or permanently delete yards</Text>
            </div>
          </Space>
        }
        placement="right"
        width={800}
        onClose={() => setShowDeletedDrawer(false)}
        open={showDeletedDrawer}
        bodyStyle={{ padding: 24 }}
      >
        {selectedDeletedKeys.length > 0 && (
          <Card 
            style={{ marginBottom: 20, borderRadius: 16 }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            <Row justify="space-between" align="middle">
              <Col>
                <Text strong style={{ fontSize: 14 }}>
                  {selectedDeletedKeys.length} yard(s) selected
                </Text>
              </Col>
              <Col>
                <Button
                  danger
                  onClick={() => {
                    Modal.confirm({
                      title: "Permanently Delete Selected Yards?",
                      icon: <WarningOutlined style={{ color: '#ff4d4f' }} />,
                      content: "This action cannot be undone. All data will be permanently removed.",
                      okText: "Yes, Delete Permanently",
                      okType: "danger",
                      cancelText: "Cancel",
                      onOk: async () => {
                        try {
                          const response = await API.HardDeleteYard(selectedDeletedKeys);
                          if (response?.status) {
                            message.success(`${selectedDeletedKeys.length} yard(s) permanently deleted.`);
                            setSelectedDeletedKeys([]);
                            fetchDeletedYards(deletedPagination.current, deletedPagination.pageSize);
                            fetchYards(pagination.current, pagination.pageSize);
                          } else {
                            message.error("Failed to delete yard(s).");
                          }
                        } catch (error) {
                          console.error("Hard delete error:", error);
                          message.error("Error deleting yard(s).");
                        }
                      },
                    });
                  }}
                  style={{ borderRadius: 12 }}
                >
                  <DeleteOutlined /> Delete Permanently
                </Button>
              </Col>
            </Row>
          </Card>
        )}

        <Table
          rowSelection={{
            selectedRowKeys: selectedDeletedKeys,
            onChange: setSelectedDeletedKeys,
          }}
          columns={deletedColumns}
          dataSource={deletedYards}
          loading={loadingDeleted}
          rowKey="_id"
          pagination={{
            current: deletedPagination.current,
            pageSize: deletedPagination.pageSize,
            total: deletedPagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {range[0]}-{range[1]} of {total} deleted yards
              </Text>
            ),
            onChange: (page, pageSize) => {
              setDeletedPagination((prev) => ({ ...prev, current: page, pageSize }));
              fetchDeletedYards(page, pageSize);
            },
          }}
          bordered={false}
          style={{ background: "#fff", borderRadius: 16 }}
        />
      </Drawer>

      <style jsx>{`
        .table-row-light {
          background: transparent;
        }
        .table-row-dark {
          background: ${token.colorBgLayout};
        }
        .table-row-light:hover,
        .table-row-dark:hover {
          background: ${token.colorPrimaryBg} !important;
          transition: all 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default ViewyardDetails;